import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

const FAQs = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      question: 'What time do we launch?',
      answer: 'Balloon flights take place at sunrise, typically launching between 6:00-6:30 AM. The exact time varies by season and is confirmed the evening before your flight. Early morning offers the best weather conditions and the most spectacular lighting for photography.'
    },
    {
      question: 'How long is the balloon flight?',
      answer: 'The balloon flight itself lasts approximately one hour. The complete experience takes about 3-4 hours, including pre-flight preparations, the flight, landing, and the champagne bush breakfast that follows.'
    },
    {
      question: 'Is it safe?',
      answer: 'Absolutely. Safety is our top priority. SKYHIGH Adventures has been operating for over 48 years with an impeccable safety record. Our British-built balloons undergo annual rigorous safety inspections by the Kenyan Civil Aviation Authority (KCAA). All our pilots are internationally certified with thousands of hours of commercial ballooning experience.'
    },
    {
      question: 'Do I need special fitness or training?',
      answer: 'No special training or exceptional fitness is required. However, passengers should be able to stand for the duration of the flight (approximately one hour) and be able to climb in and out of the balloon basket, which is about waist height. We recommend consulting your doctor if you have any health concerns.'
    },
    {
      question: 'What should I wear?',
      answer: 'Wear comfortable, casual clothing suitable for an outdoor safari. Layers are recommended as early mornings can be cool. Closed-toe shoes or boots are essential. A hat, sunglasses, and sunscreen are also recommended. The basket can get dusty during landing, so avoid white clothing.'
    },
    {
      question: 'Can I bring my camera?',
      answer: 'Yes! Cameras and phones are welcome and encouraged. The views are spectacular and you\'ll want to capture the memories. We recommend securing your camera with a strap. Video cameras and GoPros are also permitted. Please ensure batteries are fully charged as there are no charging facilities in the balloon.'
    },
    {
      question: 'What\'s the minimum age for passengers?',
      answer: 'Children must be at least 7 years old and tall enough to see over the edge of the basket (approximately 1.2m or 4 feet). All children must be accompanied by an adult. We recommend that very young children stay on the ground as the early morning start and standing for an hour can be challenging.'
    },
    {
      question: 'How many people are in the balloon?',
      answer: 'Our balloons carry between 12-16 passengers plus the pilot. This size ensures everyone has an excellent view while maintaining the intimate nature of the experience. We also offer private balloon charters for special occasions.'
    },
    {
      question: 'What happens if the weather is bad?',
      answer: 'Balloon flights are weather-dependent. If conditions are not suitable for safe flight, we will cancel and reschedule your flight at no additional charge. Our experienced team monitors weather conditions continuously and makes the final decision early on the morning of your flight. Your safety is always our first priority.'
    },
    {
      question: 'How far in advance should I book?',
      answer: 'We recommend booking as far in advance as possible, especially during peak season (July-October during the Great Migration). Flights can be booked up to 12 months in advance. However, we often have availability within a few weeks, so it\'s always worth checking.'
    },
    {
      question: 'What\'s included in the price?',
      answer: 'The price includes hotel pick-up and drop-off, pre-flight tea/coffee and snacks, the balloon flight, champagne bush breakfast after landing, and a flight certificate. You\'ll receive the complete experience from start to finish.'
    },
    {
      question: 'Is there a weight limit?',
      answer: 'For safety and balance reasons, passengers should not exceed 120kg (265 lbs). If you\'re close to or over this weight, please inform us when booking so we can make appropriate arrangements. All weights are handled discreetly and professionally.'
    },
    {
      question: 'Can pregnant women fly?',
      answer: 'For safety reasons, we do not carry passengers who are pregnant. The landing can sometimes be bumpy, and we want to ensure the safety of both mother and baby.'
    },
    {
      question: 'What wildlife will we see?',
      answer: 'From the balloon, you can see a wide variety of wildlife including elephants, lions, giraffes, zebras, wildebeest, hippos, and many more. During the Great Migration season (July-October), you may witness thousands of wildebeest and zebra crossing the plains. Every flight is different, but wildlife sightings are virtually guaranteed.'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section
        className="relative h-96 flex items-center justify-center text-white"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1489392191049-fc10c97e64b6?w=1200)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0" style={{ backgroundColor: 'rgba(101, 67, 33, 0.5)' }}></div>
        <div className="relative z-10 text-center px-4">
          <h1 className="text-5xl md:text-6xl font-bold mb-4">FAQs</h1>
          <p className="text-xl">Your questions answered</p>
        </div>
      </section>

      {/* FAQs Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="mb-12 text-center">
            <h2 className="text-3xl font-bold text-gray-800 mb-4">Frequently Asked Questions</h2>
            <p className="text-gray-600">Everything you need to know about your balloon safari adventure</p>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-xl overflow-hidden border-2 border-stone-200 hover:border-[#EE6A1F] transition-colors">
                <button
                  className="w-full px-6 py-5 text-left flex justify-between items-center hover:bg-stone-50 transition-colors"
                  onClick={() => setOpenIndex(openIndex === index ? null : index)}
                >
                  <span className="font-semibold text-gray-800 text-lg pr-8">{faq.question}</span>
                  {openIndex === index ? (
                    <ChevronUp className="w-6 h-6 flex-shrink-0" style={{ color: '#EE6A1F' }} />
                  ) : (
                    <ChevronDown className="w-6 h-6 flex-shrink-0" style={{ color: '#EE6A1F' }} />
                  )}
                </button>
                {openIndex === index && (
                  <div className="px-6 py-4 bg-stone-50 border-t-2 border-stone-200">
                    <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA Section */}
      <section className="py-20 px-4 text-white text-center" style={{ backgroundColor: '#EE6A1F' }}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-bold mb-6">Still Have Questions?</h2>
          <p className="text-xl mb-8">
            Our team is here to help answer any questions about your balloon safari experience.
          </p>
          <a
            href="/contact"
            className="inline-block font-bold px-10 py-4 rounded-lg text-lg transition-transform bg-[#EE6A1F] text-white border border-white/60 shadow-lg hover:scale-105"
          >
            CONTACT US
          </a>
        </div>
      </section>
    </div>
  );
};

export default FAQs;
